import {
  APIError,
  defaultParseError,
  fetcher,
  useClientPortalAPI,
  useSubmit,
} from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { showToast } from "@certn/cascadia-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetEmploymentVerificationTemplates } from "../employment-verification-templates/hooks";
import { packageDetailToCreatePayload } from "./package-duplicate-utils";
import { PackageDetailResponse } from "./types";

type PackageCreateResponse = {
  package_id: string;
  customer_group_id: string;
};

export function useDuplicatePackage(options?: {
  onSuccess?: (packageId: string) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDuplicating, setIsDuplicating] = useState(false);

  const { submit: submitDuplicate } = useSubmit(
    `/api/internal/client-portal/packages/create/`,
    {
      showToastOnError: true,
      onSuccess: (data: PackageCreateResponse) => {
        showToast(t("Package duplicated"), { type: "success" });
        options?.onSuccess?.(data.package_id);
        navigate(`/package/${data.package_id}`);
      },
    }
  );

  const duplicatePackage = async (packageId: string) => {
    if (isDuplicating) {
      return;
    }
    setIsDuplicating(true);
    try {
      const duplicateNamePrefix = `${t("Copy of")} `;
      const [packageDetail, packageList, availableBundles] = await Promise.all([
        fetcher.request<PackageDetailResponse>(
          `/api/internal/client-portal/packages/${packageId}/`
        ),
        fetcher.request<components["schemas"]["PaginatedPackageListList"]>(
          `/api/internal/client-portal/packages/?page_size=1000`
        ),
        fetcher.request<components["schemas"]["AccountAvailableBundles"][]>(
          `/api/internal/client-portal/customer-accounts/available-bundles/`
        ),
      ]);
      const existingNames = packageList.results.map((pkg) => pkg.name);
      await submitDuplicate(
        packageDetailToCreatePayload(
          packageDetail,
          existingNames,
          availableBundles,
          duplicateNamePrefix
        )
      );
    } catch (error) {
      const message =
        error instanceof APIError
          ? defaultParseError(error)
          : t("Something went wrong, try again");
      showToast(message, { type: "error" });
    } finally {
      setIsDuplicating(false);
    }
  };

  return { duplicatePackage, isDuplicating };
}

export function useDoesPackageContainDeactivatedTemplate(packageId: string) {
  const { data: packageData } = useClientPortalAPI(
    `/api/internal/client-portal/packages/${packageId}/`
  );
  const { deactivatedTemplateIds } = useGetEmploymentVerificationTemplates();

  const checks = packageData?.checks || [];
  const checkArguments = checks.map((check) => check.check_arguments);

  const templateIdsInPackage = checkArguments
    .map((checkArgument) => checkArgument?.template_id || null)
    .filter((id): id is string => id !== null);

  if (templateIdsInPackage.some((id) => deactivatedTemplateIds.includes(id))) {
    return true;
  }

  return false;
}
